import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Divider,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { DialogOpenImageItemDiv } from './DialogOpenImageItemDiv';
import { SetupsItemUstroistvDiv } from './SetupsItemUstroistvDiv';

interface InstructionStep {
  id: string;
  text: React.ReactNode;
  important?: boolean;
}

interface LayoutProps {
  stages: string[];
  idPrefix: string;
  intro: string;
  children: React.ReactNode;
}

export const AuthHelpRestoreLayout = ({
  stages,
  idPrefix,
  intro,
  children,
}: LayoutProps) => (
  <Stack
    spacing={3.5}
    sx={{
      width: '100%',
      minWidth: 0,
      color: '#29252f',
      fontFamily: 'Comfortaa',
      '& .MuiTypography-root': { fontFamily: 'inherit', letterSpacing: 0 },
      '& strong': { fontWeight: 700 },
      '& > section.MuiStack-root': {
        mx: -2,
        px: 2,
        py: 3,
        bgcolor: '#f7f7fa',
      },
      '& > section.MuiStack-root:nth-of-type(even)': { bgcolor: '#edf5ef' },
    }}
  >
    <Stack
      component="nav"
      aria-label="Этапы подключения подписки"
      spacing={1.5}
    >
      <Typography
        component="p"
        fontSize="13px"
        lineHeight={1.7}
        color="#66616e"
      >
        {intro}
      </Typography>
      {stages.length > 1 && (
        <Stack
          component="ol"
          spacing={1.25}
          sx={{ m: 0, p: 0, listStyle: 'none' }}
        >
          {stages.map((stage, index) => (
            <Stack
              component="li"
              key={stage}
              direction="row"
              spacing={1.25}
              alignItems="baseline"
            >
              <Typography
                component="span"
                fontSize="13px"
                fontWeight={700}
                color="#8b249f"
              >
                {index + 1}.
              </Typography>
              <Link
                href={`#${idPrefix}-${index + 1}`}
                underline="hover"
                color="inherit"
                sx={{
                  fontSize: '13px',
                  lineHeight: 1.7,
                  fontFamily: 'inherit',
                }}
              >
                {stage}
              </Link>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
    <Divider />
    {children}
  </Stack>
);

export const InstructionSetup = () => (
  <Accordion
    defaultExpanded={false}
    disableGutters
    elevation={0}
    square
    sx={{
      bgcolor: '#eaf4fb',
      borderLeft: '3px solid #699db8',
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary
      id="restore-setup-summary"
      aria-controls="restore-setup-details"
      expandIcon={<ExpandMoreIcon sx={{ color: '#3c6b88' }} />}
      sx={{
        px: 1.5,
        minHeight: 64,
        color: '#294f69',
        fontFamily: 'inherit',
        '&.Mui-focusVisible': { bgcolor: '#dbeaf6' },
      }}
    >
      <Typography
        component="h3"
        fontSize="14px"
        fontWeight={700}
        lineHeight={1.7}
      >
        Нет входа через Яндекс ID или ВК ID?
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 2 }}>
      <SetupsItemUstroistvDiv />
    </AccordionDetails>
  </Accordion>
);

export const InstructionSteps = ({ steps }: { steps: InstructionStep[] }) => (
  <Stack component="ol" spacing={2} sx={{ m: 0, p: 0, listStyle: 'none' }}>
    {steps.map(({ id, text, important }, index) => (
      <Box
        component="li"
        key={id}
        sx={{
          display: 'grid',
          gridTemplateColumns: '28px minmax(0, 1fr)',
          gap: 1.5,
          alignItems: 'start',
          ...(important && {
            p: 1.5,
            bgcolor: '#fff3dc',
            borderLeft: '3px solid #bf7b15',
          }),
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: 28,
            height: 28,
            borderRadius: '6px',
            bgcolor: important ? '#f6dfb0' : '#e8e7eb',
            color: '#45414d',
            display: 'grid',
            placeItems: 'center',
            fontSize: '13px',
            fontWeight: 700,
          }}
        >
          {index + 1}
        </Box>
        <Typography component="p" fontSize="15px" lineHeight={1.75}>
          {text}
        </Typography>
      </Box>
    ))}
  </Stack>
);

export const StageHeader = ({
  stage,
  totalStages,
  title,
  secondDevice,
}: {
  stage: number;
  totalStages: number;
  title: string;
  secondDevice: boolean;
}) => (
  <Stack spacing={1}>
    {totalStages > 1 && (
      <Typography
        component="p"
        fontSize="12px"
        fontWeight={700}
        color="#8b249f"
      >
        ЭТАП {stage} ИЗ {totalStages}
      </Typography>
    )}
    <Typography
      component="h2"
      fontSize="18px"
      fontWeight={700}
      lineHeight={1.5}
    >
      {title}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="center" color="#66616e">
      <SmartphoneOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />
      <Typography component="p" fontSize="12px" lineHeight={1.6}>
        {secondDevice ? 'На втором устройстве' : 'На устройстве с подпиской'}
      </Typography>
    </Stack>
  </Stack>
);

export const InstructionImage = ({
  image,
  label,
  aspectRatio,
}: {
  image: string;
  label: string;
  aspectRatio: string;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title={label}>
        <ButtonBase
          aria-label={label}
          onClick={() => setOpen(true)}
          sx={{
            display: 'block',
            width: '100%',
            overflow: 'hidden',
            border: '1px solid #dcd9e2',
            borderRadius: '8px',
            bgcolor: '#fff',
            '&.Mui-focusVisible': {
              outline: '3px solid #9c27b0',
              outlineOffset: '3px',
            },
          }}
        >
          <Box
            component="img"
            src={image}
            alt={label}
            sx={{
              display: 'block',
              width: '100%',
              height: 'auto',
              aspectRatio,
            }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 32,
              height: 32,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #e5e2e9',
              borderRadius: '6px',
              color: '#56505f',
            }}
          >
            <ZoomInIcon fontSize="small" />
          </Box>
        </ButtonBase>
      </Tooltip>
      <DialogOpenImageItemDiv
        open={open}
        imagePokaz={image}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
